import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Popover } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { LocalizationProvider, DateCalendar } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ButtonComponent from "../../../components/common/button/Button";
import DataTable from "../../../components/common/datatable/DataTable";
import HistoryLog from "./HistoryLog";
import { fetchQuotaMaintenanceAuditLog } from "../../../redux/actions/QuotaMaintenance";
import {
  duplicateQuotaMaintenance,
  saveQuotaMaintenance,
  submitQuotaMaintenance,
  submitQuotaWithNumber,
} from "../../../redux/actions/QuotaMaintenance/SaveQuota";

import "./QuotaArrangementGrid.css";
import { savedQuotaColumns } from "../../../redux/actions/QuotaMaintenance/SavedColumns";
import { calculateQuotaPercentage } from "../../../redux/actions/QuotaMaintenance/CalculateQuota";
import { buildQuotaCalculationRequest, applyQuotaCalculationResponse } from "./quotaCalculation";

const mapViewAllResponseToRows = (rows = []) =>
  (rows || []).map((item) => {
    // Replace explicit nulls in the API response with '-' for display purposes
    const replaceNullWithDash = (val) => {
      if (val === null) return "-";
      if (Array.isArray(val)) return val.map(replaceNullWithDash);
      if (typeof val === "object" && val !== null) {
        const out = {};
        Object.keys(val).forEach((k) => {
          out[k] = replaceNullWithDash(val[k]);
        });
        return out;
      }
      return val;
    };

    const normalized = replaceNullWithDash(item || {});
    const rowId = `header-${item?.headerId || item?.quotaNo || Math.random()}`;
    return {
      ...normalized,
      id: rowId,
      quotaNo: normalized?.quotaNo ?? "-",
      materialNo: normalized?.materialNo ?? normalized?.material ?? "",
      materialDescription: normalized?.materialDescription ?? "",
      plant:
        normalized?.plantNo && normalized?.plantDescription
          ? `${normalized.plantNo} - ${normalized.plantDescription}`
          : normalized?.plantNo ?? normalized?.plant ?? "-",
      validFrom: normalized?.validFrom ?? "",
      validTo: normalized?.validTo ?? "",
      totalMaxQty: normalized?.totalMaxQty ?? "",
      lastEdited: normalized?.lastEdited ?? "",
      editedBy: normalized?.editedBy ?? "",
      status: normalized?.status ?? "",
      errorDetails: normalized?.errorDetails ?? "",
      md04: normalized?.md04 ?? "MD04",
      vendors: (normalized?.vendors ?? normalized?.children ?? []).map((vendor) => ({
        ...vendor,
        id: `vendor-${vendor?.vendorNo ?? vendor?.itemId ?? Math.random()}`,
        vendorNo: vendor?.vendorNo ?? "",
        vendorName: vendor?.vendorName ?? "",
        procurementType: vendor?.procurementType ?? "",
        specialProcurementType: vendor?.specialProcurementType ?? "-",
        maxQty: vendor?.maxQty ?? "",
        quotaPercent: vendor?.quotaPercent ?? vendor?.quotaPct ?? "",
        quotaBaseQty: vendor?.quotaBaseQty ?? "",
        minLotSize: vendor?.minLotSize ?? "",
        maxLotSize: vendor?.maxLotSize ?? "",
        allocatedQty: vendor?.allocatedQty ?? "",
        status: vendor?.status ?? "",
        errorDetails: vendor?.errorDetails ?? "",
        // Map contracts for this vendor into a dedicated array
        contracts: (Array.isArray(vendor?.contracts) ? vendor.contracts : []).map((c, idx) => ({
          id: `contract-${c?.contractItem ?? c?.contractNo ?? idx}`,
          contractNo: c?.contractNo ?? "",
          contractItem: c?.contractItem ?? "",
          contractType: c?.contractType ?? "",
          contractValidFrom: c?.validFrom ?? c?.contractValidFrom ?? "",
          contractValidTo: c?.contractValidTo ?? c?.validTo ?? "",
          pricingValidFrom: c?.pricingValidFrom ?? "",
          pricingValidTo: c?.pricingValidTo ?? "",
          allocatedQty: c?.allocatedQty ?? "",
          scaledQty: c?.scaledQty ?? "",
          itemStatus: c?.itemStatus ?? c?.status ?? "",
        })),
      })),
    };
  });

const mapRowsToSaveRequests = (rows = []) => {
  const sanitizeForSave = (value, isTopLevel = false) => {
    if (value === "-") return null;
    if (Array.isArray(value)) return value.map((v) => sanitizeForSave(v, false));
    if (value && typeof value === "object") {
      const out = {};
      Object.keys(value).forEach((k) => {
        // drop UI-only fields
        if (k === "id" || k === "md04") return;
        // drop top-level quota.contractType (but keep contractType inside contract objects)
        if (isTopLevel && k === "contractType") return;
        out[k] = sanitizeForSave(value[k], false);
      });
      return out;
    }
    return value;
  };

  return (rows || []).map((row) => sanitizeForSave({
    ...row,
    vendors: (row.vendors || []).map(({ quotaPercent, ...vendor }) => ({
      ...vendor,
      // Map the grid-only alias to the API field; never send quotaPercent.
      quotaPct: quotaPercent !== undefined ? quotaPercent : vendor.quotaPct,
    })),
  }, true));
};

const getPlantNo = (row) =>
  row?.plantNo || String(row?.plant || "").split(" - ")[0].trim();

const formatQuotaDate = (value) => {
  if (!value) return "";

  const asStr = String(value);
  const oDataDateMatch = asStr.match(/^\/Date\((\d+)\)\/$/);

  let parsed;
  if (oDataDateMatch) {
    parsed = dayjs(Number(oDataDateMatch[1]));
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(asStr)) {
    // Treat plain YYYY-MM-DD as UTC start of day
    parsed = dayjs(`${asStr}T00:00:00Z`);
  } else {
    parsed = dayjs(asStr);
  }

  if (!parsed.isValid()) return value;

  const ms = parsed.valueOf();
  return `/Date(${ms})/`;
};

const mapRowToSubmitRequest = (row, submitterEmail) => ({
  Material: row?.materialNo || "",
  Plant: getPlantNo(row),
  ValidityStartDate: formatQuotaDate(row?.validFrom),
  ValidityEndDate: formatQuotaDate(row?.validTo),
  to_PurgQuotaArrangementItem: {
    results: (row?.vendors || []).map((vendor) => ({
      MaterialProcurementCategory: vendor?.procurementType || "",
      MaterialProcurementType: vendor?.specialProcurementType || 0,
      Supplier: vendor?.vendorNo || "",
      SupplyingPlant: "",
      PurchasingSourceQuota: vendor?.quotaPercent || "0",
      QuotaBaseQuantity: vendor?.quotaBaseQty || "0",
      QuotaMaximumQuantity: vendor?.maxQty || "0",
      MaterialMinLotSizeQuantity: vendor?.minLotSize || "0",
      MaterialMaxLotSizeQuantity: vendor?.maxLotSize || "0",
      ProductionVersion: "",
    })),
  },
  submitterEmail: submitterEmail || "",
});

// Recursively convert UI '-' placeholders back to null for API payloads
const sanitizeDashToNull = (val) => {
  if (val === "-") return null;
  if (Array.isArray(val)) return val.map(sanitizeDashToNull);
  if (val && typeof val === "object") {
    const out = Array.isArray(val) ? [] : {};
    Object.keys(val).forEach((k) => {
      out[k] = sanitizeDashToNull(val[k]);
    });
    return out;
  }
  return val;
};

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .replace(/[^a-z0-9]/gi, "")
    .toUpperCase();

const getApiErrorMessage = (error, fallbackMessage) => {
  if (!error) return fallbackMessage;
  if (typeof error === "string") return error;
  return error?.errorMessage || error?.message || fallbackMessage;
};

function VendorInput({ value }) {
  return <div className="vendor-input">{value}</div>;
}

function SelectCellRenderer(params) {
  const expanded = !!params?.node?.expanded;

  const toggle = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const node = params.node;
    node.setExpanded(!node.expanded);
    try {
      if (params.api && typeof params.api.redrawRows === 'function') {
        params.api.redrawRows({ rowNodes: [node] });
      }
      try {
        if (params.api && typeof params.api.resetRowHeights === 'function') params.api.resetRowHeights();
      } catch (e) {}
      try {
        window.dispatchEvent(new Event('resize'));
      } catch (e) {}
      // No longer dispatching custom events; rely on AG Grid onRowExpanded
    } catch (err) {}
  };

  return (
    <span
      className="expand-arrow"
      onClick={toggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(e); } }}
      role="button"
      tabIndex={0}
      aria-label={expanded ? "Collapse" : "Expand"}
    >
      {expanded ? "▼" : "▶"}
    </span>
  );
}

function DateCellRenderer(params) {
  const [anchorEl, setAnchorEl] = useState(null);

  const isEditable =
    typeof params.colDef?.editable === "function"
      ? params.colDef.editable(params)
      : Boolean(params.colDef?.editable);

  const handleOpen = (e) => {
    e.stopPropagation();
    if (!isEditable) return;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleChange = (newValue) => {
    if (newValue) {
      // Preserve whichever format the column already uses
      const isISO = params.value && /^\d{4}-\d{2}-\d{2}$/.test(params.value);
      const formatted = newValue.format(isISO ? "YYYY-MM-DD" : "MM/DD/YYYY");
      params.node.setDataValue(params.column.getColId(), formatted);
    }
    handleClose();
  };

  const parsed = params.value ? dayjs(params.value) : null;

  return (
    <>
      <div className="date-cell-renderer" title={""}>
        <span>{params.value}</span>
        <CalendarTodayIcon
          fontSize="small"
          className="calendar-icon"
          onClick={handleOpen}
          style={{ cursor: isEditable ? "pointer" : "not-allowed", color: isEditable ? undefined : "#9ca3af" }}
          aria-disabled={!isEditable}
        />
      </div>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar value={parsed} onChange={handleChange} />
        </LocalizationProvider>
      </Popover>
    </>
  );
}

function EditableCellRenderer(params) {
  const startEdit = (e) => {
    e.stopPropagation();
    params.api.startEditingCell({
      rowIndex: params.node.rowIndex,
      colKey: params.column.getColId(),
    });
  };

  return (
    <div className="editable-cell-renderer">
      <span>{params.value}</span>
      <EditIcon
        fontSize="small"
        className="pencil-icon"
        onClick={startEdit}
      />
    </div>
  );
}

function Md04CellRenderer(params) {
  const material = params.data?.materialNo || "";
  const plant = getPlantNo(params.data);
  const url = `${process.env.REACT_APP_SAP_PO_BASE_URL}#MRPMaterial-monitorSupplyAndDemand?Material=${material}&MRPPlant=${plant}`;

  return (
    <a
      href={url}
      className="md04-link"
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
    >
      MD04
    </a>
  );
}



function DetailGrid({ data, onVendorCellValueChanged, parentGridApi, onVendorEditStopped, onAnyCellEditingStarted, onAnyCellEditingStopped, isCalculating }) {
  // Simplified: rely on AG Grid's detailRowAutoHeight + domLayout='autoHeight'.
  // Remove manual height bookkeeping; just trigger a height recalculation on expand.
  const handleRowExpanded = (event) => {
    try {
      if (event?.api && typeof event.api.resetRowHeights === 'function') {
        event.api.resetRowHeights();
      }
    } catch (e) {}
    try {
      if (parentGridApi && typeof parentGridApi.resetRowHeights === 'function') {
        parentGridApi.resetRowHeights();
      }
    } catch (e) {}
    try { window.dispatchEvent(new Event('resize')); } catch (e) {}
  };

  const detailColumnDefs = useMemo(
    () => [
      {
        headerName: "SELECT",
        width: 110,
        pinned: "left",
        sortable: false,
        resizable: false,
        cellRenderer: SelectCellRenderer,
        cellClass: "select-cell",
        filter: false,
      },
      {
        headerName: "VENDOR NO.",
        field: "vendorNo",
        width: 200,
        cellRenderer: VendorInput,
      },
      {
        headerName: "VENDOR NAME",
        field: "vendorName",
        width: 270,
        cellRenderer: VendorInput,
      },
      {
        headerName: "PROCUREMENT TYPE",
        field: "procurementType",
        width: 210,
        editable: !isCalculating,
        cellClass: "editable-cell",
        cellRenderer: EditableCellRenderer,
      },
      {
        headerName: "SPECIAL PROCUREMENT TYPE",
        field: "specialProcurementType",
        width: 210,
        editable: false,
        cellClass: "material-number",
        cellRenderer: VendorInput,
      },
      {
        headerName: "MAX QTY",
        field: "maxQty",
        width: 160,
        editable: !isCalculating,
        cellClass: "editable-cell",
        cellRenderer: EditableCellRenderer,
     //   type: "numericColumn",
        valueFormatter: (params) => {
          const v = params?.value;
          if (v === null || v === undefined || v === "" || v === "-") return "-";
          const n = Number(v);
          return Number.isFinite(n) ? String(v) : "-";
        },
      },
      {
        headerName: "QUOTA %",
        field: "quotaPercent",
        width: 150,
        editable: !isCalculating,
        cellClass: "editable-cell",
        cellRenderer: EditableCellRenderer,
      //  type: "numericColumn",
        valueFormatter: (params) => {
          const v = params?.value;
          if (v === null || v === undefined || v === "" || v === "-") return "-";
          const n = Number(v);
          return Number.isFinite(n) ? String(v) : "-";
        },
      },
      {
        headerName: "QUOTA BASE QTY",
        field: "quotaBaseQty",
        width: 180,
        editable: !isCalculating,
        cellClass: "editable-cell",
        cellRenderer: EditableCellRenderer,
      //  type: "numericColumn",
        valueFormatter: (params) => {
          const v = params?.value;
          if (v === null || v === undefined || v === "" || v === "-") return "-";
          const n = Number(v);
          return Number.isFinite(n) ? String(v) : "-";
        },
      },
      {
        headerName: "MIN LOT SIZE",
        field: "minLotSize",
        width: 170,
        editable: !isCalculating,
        cellClass: "editable-cell",
        cellRenderer: EditableCellRenderer,
       // type: "numericColumn",
        valueFormatter: (params) => {
          const v = params?.value;
          if (v === null || v === undefined || v === "" || v === "-") return "-";
          const n = Number(v);
          return Number.isFinite(n) ? String(v) : "-";
        },
      },
      {
        headerName: "MAX LOT SIZE",
        field: "maxLotSize",
        width: 170,
        editable: !isCalculating,
        cellClass: "editable-cell",
        cellRenderer: EditableCellRenderer,
        //type: "numericColumn",
        valueFormatter: (params) => {
          const v = params?.value;
          if (v === null || v === undefined || v === "" || v === "-") return "-";
          const n = Number(v);
          return Number.isFinite(n) ? String(v) : "-";
        },
      },
      {
        headerName: "STATUS",
        field: "status",
        width: 180,
        cellClass: "material-number",
      },
      {
        headerName: "ERROR DETAILS",
        field: "errorDetails",
        width: 180,
        cellClass: "material-number error-details-cell",
        tooltipField: "errorDetails",
      },
    ],
    [isCalculating]
  );

  return (
    <div className="detail-wrapper">
      <div
        className="ag-theme-quartz detail-grid"
        style={{ width: "100%", overflowX: "auto" }}
      >
        <DataTable
          rows={data.vendors}
          columns={detailColumnDefs}
          paginationProp={false}
          defaultColDef={{
            sortable: false,
            filter: "agMultiColumnFilter",
            resizable: false,
            wrapHeaderText: true,
          }}
          onCellValueChanged={(params) => onVendorCellValueChanged(data, params)}
          onCellEditingStarted={(params) => onAnyCellEditingStarted?.(params)}
          onCellEditingStopped={(params) => {
            try { onAnyCellEditingStopped?.(params); } catch (e) {}
            onVendorEditStopped?.(data, params);
          }}
          onRowExpanded={handleRowExpanded}
          gridOptions={{
            domLayout: 'autoHeight',
            masterDetail: true,
            detailCellRenderer: (params) => (
              <div style={{ padding: 8 }}>
                <div style={{ width: '100%' }}>
                  <ContractGrid
                    vendor={params.data}
                    vendorGridApi={params.api}
                    parentGridApi={parentGridApi}
                    // Ensure we pass the header row (`data`) as the parentRow so
                    // markRowAsUpdated records changes under the header id (required
                    // for submitQuotaWithNumber payload lookup).
                    onContractCellValueChanged={(vendorRow, prm) => onVendorCellValueChanged(data, prm)}
                    onVendorEditStopped={(vendorRow, prm) => onVendorEditStopped?.(data, prm)}
                    onAnyCellEditingStarted={onAnyCellEditingStarted}
                    onAnyCellEditingStopped={onAnyCellEditingStopped}
                  />
                </div>
              </div>
            ),
            detailRowAutoHeight: true,
            headerHeight: 46,
            rowHeight: 72,
            alwaysShowHorizontalScroll: true,
            enableBrowserTooltips: true,
            tooltipShowDelay: 0,
            suppressMovableColumns: true,
          }}
        />
      </div>
    </div>
  );
}

function ContractGrid({ vendor, onContractCellValueChanged, parentGridApi, vendorGridApi, onVendorEditStopped, onAnyCellEditingStarted, onAnyCellEditingStopped }) {
  const cols = useMemo(() => [
    { headerName: 'CONTRACT NO.', field: 'contractNo', width: 160 },
    { headerName: 'CONTRACT ITEM', field: 'contractItem', width: 170 },
    { headerName: 'CONTRACT TYPE', field: 'contractType', width: 170 },
    { headerName: 'CONTRACT VALID FROM', field: 'contractValidFrom', width: 200,},
    { headerName: 'CONTRACT VALID TO', field: 'contractValidTo', width: 200,},
    { headerName: 'PRICING VALID FROM', field: 'pricingValidFrom', width: 200,  },
    { headerName: 'PRICING VALID TO', field: 'pricingValidTo', width: 200,  },
    { headerName: 'ALLOCATED QTY', field: 'allocatedQty', width: 180,  },
      // { headerName: 'SCALED QTY', field: 'scaledQty', width: 180, },
    { headerName: 'ITEM STATUS', field: 'itemStatus', width: 160 },
  ], []);
  // Let AG Grid autoHeight determine the contract grid height.

  const [localApi, setLocalApi] = useState(null);

  useEffect(() => {
    // when number of rows changes, request vendor and parent grids to recompute heights
    const t = setTimeout(() => {
      try {
        if (vendorGridApi && typeof vendorGridApi.resetRowHeights === 'function') vendorGridApi.resetRowHeights();
      } catch (e) {}
      try {
        if (parentGridApi && typeof parentGridApi.resetRowHeights === 'function') {
          setTimeout(() => {
            try {
              parentGridApi.resetRowHeights();
            } catch (e) {}
          }, 60);
        }
      } catch (e) {}
      try { window.dispatchEvent(new Event('resize')); } catch (e) {}
    }, 50);
    return () => clearTimeout(t);
  }, [vendor?.contracts?.length, vendorGridApi, parentGridApi]);

  const handleGridReady = (params) => {
    const api = params.api; // this is the contract grid api
    setLocalApi(api);
    // size columns to fit the available width, then tell vendor and parent grids to recompute heights
    setTimeout(() => {
      try {
        // don't auto-fit columns; allow horizontal scroll when columns exceed width
      } catch (e) {}
      try {
        if (vendorGridApi && typeof vendorGridApi.resetRowHeights === 'function') vendorGridApi.resetRowHeights();
      } catch (e) {}
      try {
        if (parentGridApi && typeof parentGridApi.resetRowHeights === 'function') {
          setTimeout(() => {
            try {
              parentGridApi.resetRowHeights();
            } catch (e) {}
          }, 60);
        }
      } catch (e) {}
      window.dispatchEvent(new Event('resize'));
    }, 50);
  };

  return (
    <div className="ag-theme-quartz" style={{ width: '100%', overflowX: 'auto' }}>
      <DataTable
        rows={vendor?.contracts || []}
        columns={cols}
        paginationProp={false}
        defaultColDef={{
          sortable: false,
          filter: false,
          resizable: false,
          suppressMenu: true,
          menuTabs: [],
          headerClass: 'contract-header',
        }}
        onCellValueChanged={(params) => onContractCellValueChanged(vendor, params)}
        onCellEditingStarted={(params) => onAnyCellEditingStarted?.(params)}
        onCellEditingStopped={(params) => {
          try { onAnyCellEditingStopped?.(params); } catch (e) {}
          onVendorEditStopped?.(vendor, params);
        }}
        onGridReady={handleGridReady}
        gridOptions={{ domLayout: 'autoHeight', headerHeight: 46, rowHeight: 64, suppressMovableColumns: true, alwaysShowHorizontalScroll: true }}
      />
    </div>
  );
}

export default function QuotaArrangementGrid({
  statusFilter = "",
  onDuplicateSuccess,
}) {
  const dispatch = useDispatch();
  const quotaMaintenanceData = useSelector(
    (state) => state?.QuotaMaintenanceSlice?.data?.data,
  );
  const historyLogData = useSelector(
    (state) => state?.QuotaMaintenanceSlice?.historyLogData,
  );
  const historyLoading = useSelector(
    (state) => state?.QuotaMaintenanceSlice?.historyLoading,
  );
  const saveLoading = useSelector(
    (state) => state?.SaveQuotaSlice?.loading,
  );
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isHistoryLogOpen, setIsHistoryLogOpen] = useState(false);
  const [updatedRowsById, setUpdatedRowsById] = useState({});
  const [updatedChangesById, setUpdatedChangesById] = useState({});
  const [pendingCalculationById, setPendingCalculationById] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const dataVersion = useRef(0);
  const hasPendingCalculation = Object.keys(pendingCalculationById).length > 0;
  const procurementDivisionType = localStorage.getItem("procurementDivisionType");
  const [editingCount, setEditingCount] = useState(0);

  const handleCellEditingStarted = useCallback(() => {
    setEditingCount((c) => c + 1);
  }, []);

  const handleCellEditingStopped = useCallback(() => {
    setEditingCount((c) => Math.max(0, c - 1));
  }, []);

  const isAnyCellEditing = editingCount > 0;

  const selectedHasUnsavedRows = useMemo(
    () => (selectedRows || []).some((r) => Boolean(updatedRowsById?.[r?.id])),
    [selectedRows, updatedRowsById]
  );

  useEffect(() => {
    dataVersion.current += 1;
    setRowData(mapViewAllResponseToRows(quotaMaintenanceData || []));
    setSelectedCount(0);
    setSelectedRows([]);
    setUpdatedRowsById({});
    setUpdatedChangesById({});
    setPendingCalculationById({});
  }, [quotaMaintenanceData]);

  const masterPaginationPageSize = 10;
  const masterGridHeight = 550;
  const updatedRows = useMemo(() => Object.values(updatedRowsById), [updatedRowsById]);
  const hasUpdatedRows = updatedRows.length > 0;
  const filteredRowData = useMemo(() => {
    const normalizedStatusFilter = normalizeStatus(statusFilter);

    if (!normalizedStatusFilter) return rowData;

    return rowData.filter(
      (row) => normalizeStatus(row?.status) === normalizedStatusFilter,
    );
  }, [rowData, statusFilter]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "SELECT",
        width: 120,
        pinned: "left",
        sortable: false,
        resizable: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        cellRenderer: SelectCellRenderer,
        cellClass: "select-cell",
        filter: false,
      },
      {
        headerName: "QUOTA NO.",
        field: "quotaNo",
        width: 175,
      },
      {
        headerName: "MATERIAL NO.",
        field: "materialNo",
        width: 170,
        cellClass: "material-number",
      },
      {
        headerName: "MATERIAL DESCRIPTION",
        field: "materialDescription",
        width: 300,
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "PLANT",
        field: "plant",
        
        cellClass: "material-number",
      },
      {
        headerName: "VALID FROM",
        field: "validFrom",
        width: 220,
        
      },
      {
        headerName: "VALID TO",
        field: "validTo",
        width: 220,
        editable: !isCalculating,
        cellClass: "editable-cell",
        cellRenderer: DateCellRenderer,
        headerTooltip: "Validity end date can only be edited from sap if QuotaBaseQty and QuotaAllocatedQty is 0.",
        headerClass: "header-with-info",
      },
     
      {
        headerName: "TOTAL MAX QTY",
        field: "totalMaxQty",
        width: 180,
        cellClass: "material-number",
        valueFormatter: (params) => {
          const v = params?.value;
          if (v === null || v === undefined || v === "" || v === "-") return "-";
          const n = Number(v);
          return Number.isFinite(n) ? String(v) : "-";
        },
      },
      {
        headerName: "LAST EDITED",
        field: "lastEdited",
        width: 180,
        cellClass: "material-number",
      },
      {
        headerName: "EDITED BY",
        field: "editedBy",
        width: 180,
        cellClass: "material-number",
      },
      {
        headerName: "STATUS",
        field: "status",
        width: 180,
        cellClass: "material-number",
      },
      {
        headerName: "ERROR DETAILS",
        field: "errorDetails",
        width: 180,
        cellClass: "material-number error-details-cell",
        tooltipField: "errorDetails",
      },
      {
        headerName: "MD04",
        field: "md04",
        width: 180,
        cellClass: "material-number",
        cellRenderer: Md04CellRenderer,
      }
    ],
    [isCalculating]
  );

  // AG Grid passes the full params object; adapt it to DetailGrid's expected prop
  const markRowAsUpdated = (row, changes = {}) => {
    try {
      // debug log
      // eslint-disable-next-line no-console
      console.debug("markRowAsUpdated: ", row?.id, changes);
    } catch (e) {}

    setUpdatedRowsById((prev) => ({
      ...prev,
      [row.id]: row,
    }));

    setUpdatedChangesById((prev) => {
      const existing = prev[row.id] || {};
      // merge header
      const mergedHeader = { ...(existing.header || {}), ...(changes.header || {}) };

      // merge items: items is object keyed by quotaArrangementItem
      const mergedItems = { ...(existing.items || {}) };
      if (changes.items) {
        Object.keys(changes.items).forEach((k) => {
          mergedItems[k] = {
            ...(mergedItems[k] || {}),
            ...(changes.items[k] || {}),
            body: { ...(mergedItems[k]?.body || {}), ...(changes.items[k]?.body || {}) },
          };
        });
      }

      const next = {
        ...prev,
        [row.id]: {
          headerId: row?.headerId ?? existing.headerId ?? null,
          header: mergedHeader,
          items: mergedItems,
        },
      };
      try {
        // eslint-disable-next-line no-console
        console.debug('updatedChangesById after markRowAsUpdated:', row.id, next[row.id]);
      } catch (e) {}
      return next;
    });
  };

  const onMasterCellValueChanged = (params) => {
    if (params.oldValue === params.newValue) return;
    // If Valid To edited, record header change for API
    const col = params.column.getColId?.() || params.colDef?.field;
    const changes = {};
    if (col === "validTo") {
      changes.header = { ValidityEndDate: formatQuotaDate(params.newValue) };
    }

    markRowAsUpdated(params.data, changes);
  };

  const onVendorCellValueChanged = (parentRow, params) => {
    try {
      // eslint-disable-next-line no-console
      console.debug('onVendorCellValueChanged parentRow:', parentRow?.id, 'field:', params?.colDef?.field, 'old:', params?.oldValue, 'new:', params?.newValue);
    } catch (e) {}
    if (params.oldValue === params.newValue) return;

    const field = params.column.getColId?.() || params.colDef?.field;
    const data = params.data || {};
    const changes = {};

    if (field === "maxQty" && Array.isArray(data.contracts)) {
      setPendingCalculationById((prev) => ({ ...prev, [parentRow.id]: true }));
    }

    // If the changed row is a contract (has contractItem), map contract-level fields
    if (!Array.isArray(data.contracts) && (data.contractItem !== undefined || data.contractNo !== undefined)) {
      const contractKey = String(data.contractItem || data.contractNo || "");
      const contractMapping = {
        contractNo: "ContractNo",
        contractItem: "ContractItem",
        contractValidFrom: "ContractValidFrom",
        contractValidTo: "ContractValidTo",
        pricingValidFrom: "PricingValidFrom",
        pricingValidTo: "PricingValidTo",
        allocatedQty: "AllocatedQty",
        scaledQty: "ScaledQty",
        itemStatus: "ItemStatus",
      };

      const apiKey = contractMapping[field];
      if (apiKey) {
        changes.items = {
          [contractKey]: {
            body: {
              [apiKey]: String(params.newValue ?? ""),
            },
          },
        };
      }
    } else {
      // Vendor-level change
      data[field] = params.newValue;
      if (field === "quotaPercent") data.quotaPct = params.newValue;
      // Explicitly carry the edited detail row into the header saved by the API.
      // Do not rely on AG Grid detail data sharing the same object reference.
      parentRow.vendors = (parentRow.vendors || []).map((vendor) =>
        vendor === data || vendor.id === data.id
          ? { ...vendor, ...data }
          : vendor,
      );
      const vendorKey = String(data?.itemId || data?.vendorNo || "");
      const vendorMapping = {
        maxQty: "QuotaMaximumQuantity",
        quotaPercent: "PurchasingSourceQuota",
        quotaBaseQty: "QuotaBaseQuantity",
        minLotSize: "MaterialMinLotSizeQuantity",
        maxLotSize: "MaterialMaxLotSizeQuantity",
        vendorNo: "Supplier",
        procurementType: "MaterialProcurementCategory",
      };

      const apiKey = vendorMapping[field];
      if (apiKey) {
        changes.items = {
          [vendorKey]: {
            body: {
              [apiKey]: String(params.newValue ?? ""),
            },
          },
        };
      }
    }

    // If there were mapped changes, record them
    if (changes.items) markRowAsUpdated(parentRow, changes);

    // Refresh master row cells so editable state for Valid From/To is recalculated
    try {
      const node = gridApi?.getRowNode(String(parentRow.id));
      if (node) {
        gridApi.refreshCells({ rowNodes: [node], columns: ["validFrom", "validTo"] });
      }
    } catch (e) {
      // ignore refresh errors
    }
  };

  const DetailCellRenderer = (params) => (
    <DetailGrid
      data={params.data}
      onVendorCellValueChanged={onVendorCellValueChanged}
      parentGridApi={gridApi}
      onAnyCellEditingStarted={handleCellEditingStarted}
      onAnyCellEditingStopped={handleCellEditingStopped}
      isCalculating={isCalculating}
      onVendorEditStopped={(parentRow, params) => {
        if (params.oldValue !== params.newValue) {
          onVendorCellValueChanged(parentRow, params);
        }
      }}
    />
  );

  const onSelectionChanged = (params) => {
    const rows = params.api.getSelectedRows() || [];
    setSelectedRows(rows);
    setSelectedCount(rows.length);
  };

  const calculateUpdatedRows = async () => {
    if (!hasPendingCalculation || isCalculating || saveLoading || isAnyCellEditing) return;
    const version = dataVersion.current;
    setIsCalculating(true);
    try {
      // The endpoint accepts one header per request, including all its vendors.
      const requests = Object.keys(pendingCalculationById).map((id) => {
        const row = updatedRowsById[id] || rowData.find((entry) => entry.id === id);
        return { row, data: buildQuotaCalculationRequest(row) };
      });
      for (const { row, data } of requests) {
        const response = await dispatch(calculateQuotaPercentage({ data })).unwrap();
        if (dataVersion.current !== version) return;
        const calculatedRow = applyQuotaCalculationResponse(row, response);
        const items = {};
        calculatedRow.vendors.forEach((vendor) => {
          const key = String(vendor.itemId || vendor.vendorNo || "");
          items[key] = { body: {
            QuotaMaximumQuantity: String(vendor.maxQty),
            PurchasingSourceQuota: String(vendor.quotaPercent),
          } };
        });
        markRowAsUpdated(calculatedRow, { items });
        setRowData((prev) => prev.map((entry) => entry.id === row.id ? calculatedRow : entry));
        setSelectedRows((prev) => prev.map((entry) => entry.id === row.id ? calculatedRow : entry));
        setPendingCalculationById((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
      }
      toast.success("Quota percentages calculated successfully. You can now save the changes.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to calculate quota percentages"));
    } finally {
      setIsCalculating(false);
    }
  };

  const duplicateSelected = async () => {
    const selected = gridApi?.getSelectedRows() || selectedRows;
    if (!selected.length) return;

    const userId = localStorage.getItem("data");
    try {
      const response = await dispatch(
        duplicateQuotaMaintenance({
          params: { userId },
          data: mapRowsToSaveRequests(selected),
        })
      ).unwrap();
      await onDuplicateSuccess?.();
      toast.success(response || "Duplicate line item created successfully");
      gridApi?.deselectAll();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to duplicate quota records"));
    }
  };

  const submitSelected = async () => {
    const selected = gridApi?.getSelectedRows() || selectedRows;
    try {
      // eslint-disable-next-line no-console
      console.debug('submitSelected invoked, selectedRows:', selected.map(s => s.id));
    } catch (e) {}
    if (!selected.length) return;

    const submitterEmail = localStorage.getItem("data");
    try {
      // Split selected rows into those without quotaNumber (call existing batch API)
      // and those with quotaNumber where we need to call the new /api/quotas endpoint
      const hasQuotaNumber = (r) => Boolean(r?.quotaNo && String(r.quotaNo).trim() && String(r.quotaNo) !== "-");
      const rowsWithQuotaNumber = selected.filter((r) => hasQuotaNumber(r));
      const rowsWithoutQuotaNumber = selected.filter((r) => !hasQuotaNumber(r));

      // If user selected a mix of rows with and without quotaNumber, do not call any API — show a toast
      if (rowsWithQuotaNumber.length > 0 && rowsWithoutQuotaNumber.length > 0) {
        toast.error(
          "Please submit the records either with quotaNumber or without Quota Number, you can't submit both together.",
          { autoClose: 5500 },
        );
        return;
      }

      // Call existing endpoint for rows without quotaNumber
      if (rowsWithoutQuotaNumber.length) {
        // send array payload even for single row(s)
        const payloadForOldApi = rowsWithoutQuotaNumber.map((row) =>
          mapRowToSubmitRequest(row, submitterEmail)
        );
        const response = await dispatch(
          submitQuotaMaintenance({ data: sanitizeDashToNull(payloadForOldApi) })
        ).unwrap();
        toast.success(response || "Quota updates submitted successfully");
      }

      const savedChangesById = rowsWithQuotaNumber.length
        ? (await dispatch(savedQuotaColumns()).unwrap()) || {}
        : {};

      // Build payload for new API for rows with quotaNumber — include only rows that have header or item changes
      const payloadForQuotaApi = [];
      rowsWithQuotaNumber.forEach((row) => {
        const changes = savedChangesById[row.id] || {};
        try {
          // eslint-disable-next-line no-console
          console.debug(
            "submitSelected checking row",
            row.id,
            "quotaNo:",
            row.quotaNo,
            "hasChanges:",
            Boolean(
              (changes.header && Object.keys(changes.header || {}).length) ||
                (changes.items && Object.keys(changes.items || {}).length)
            )
          );
        } catch (e) {}

        const hasHeaderChanges = Boolean(changes.header && Object.keys(changes.header || {}).length);
        const hasItemChanges = Boolean(changes.items && Object.keys(changes.items || {}).length);

        const entry = {
          quotaNumber: String(row.quotaNo),
          submitterEmail: submitterEmail || "",
        };

        // Only include autoZeroAllItems when header-level changes exist
        if (hasHeaderChanges) entry.autoZeroAllItems = true;

        if (!hasHeaderChanges && !hasItemChanges) {
          // User selected the row without making edits — send the validity and
          // the full item bodies for each vendor so the quotas API has a
          // complete payload to process.
          entry.header = { ValidityEndDate: formatQuotaDate(row?.validTo) };

          entry.items = (row?.vendors || []).map((vendor, idx) => ({
            quotaArrangementItem: vendor?.quotaPosition,
            body: {
            //  Supplier: vendor?.vendorNo || "",
              MaterialProcurementCategory: vendor?.procurementType || "",
              PurchasingSourceQuota: vendor?.quotaPercent || vendor?.quotaPct || "0",
            //  QuotaBaseQuantity: vendor?.quotaBaseQty || "0",
              QuotaMaximumQuantity: vendor?.maxQty || "0",
              MaterialMinLotSizeQuantity: vendor?.minLotSize || "0",
              MaterialMaxLotSizeQuantity: vendor?.maxLotSize || "0",
              // include contracts if present so API receives full item context
             
            },
          }));
        } else {
          if (hasHeaderChanges) {
            entry.header = { ...changes.header };
          }

          if (hasItemChanges) {
            entry.items = Object.keys(changes.items).map((quotaArrangementItem) => ({
              quotaArrangementItem: String(quotaArrangementItem),
              body: { ...(changes.items[quotaArrangementItem].body || {}) },
            }));
          }
        }

        payloadForQuotaApi.push(sanitizeDashToNull(entry));
      });

      // If user selected rows that already have a quota number but none of them
      // contain header/vendor changes, do not call the quotas API and show a warning.
 //     if (rowsWithQuotaNumber.length && payloadForQuotaApi.length === 0) {
  //      toast.warn("You need to update something for submitting to SAP", { autoClose: 5000 });
  //      return;
 //     }

      if (payloadForQuotaApi.length) {
        const response = await dispatch(submitQuotaWithNumber({ data: sanitizeDashToNull(payloadForQuotaApi) }));
        // response may be fulfilled or rejected
        if (response?.meta?.requestStatus === "fulfilled") {
          toast.success("Quota updates submitted successfully");
        } else {
          toast.error(getApiErrorMessage(response?.payload, "Failed to submit quota updates"));
        }
      }

      gridApi?.deselectAll();
      // refresh results after submit
      await onDuplicateSuccess?.();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to submit quota records"));
    }
  };

  const saveUpdatedRows = async () => {
    console.log('updatedChangesById', updatedChangesById)
    if (!hasUpdatedRows || isCalculating) return;
    gridApi?.stopEditing();
    const userId = localStorage.getItem("data");
    try {
      const response = await dispatch(
        saveQuotaMaintenance({
          params: { userId },
          data: mapRowsToSaveRequests(updatedRows),
        })
      ).unwrap();
      toast.success(response || "Quota records saved successfully");
      // localStorage.setItem(
      //   "quotaMaintenanceUpdatedRows",
      //   JSON.stringify(updatedChangesById),
      // );
      dispatch(savedQuotaColumns({
        data: updatedChangesById,
      }))
      setUpdatedRowsById({});
      setPendingCalculationById({});
      gridApi?.deselectAll();
      // refresh results (calls search or viewAll depending on parent state)
      await onDuplicateSuccess?.();
    } catch (error) {
      toast.error(error?.message || error || "Failed to save quota records");
    }
  };

  const auditLog = async () => {
    setIsHistoryLogOpen(true);
    try {
      await dispatch(fetchQuotaMaintenanceAuditLog({  })).unwrap();
    } catch (error) {
      toast.error(error?.message || "Failed to fetch audit logs");
    }
  };

  const closeHistoryLog = () => {
    setIsHistoryLogOpen(false);
  };

  return (
    <div style={{ padding: "0px", background: "#f4f4f4" }}>
      {/* ================= HEADER ================= */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#fff",
          padding: "16px 20px",
          borderRadius: "10px 10px 0 0",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: 700,
            color: "#111827",
            lineHeight: 1.5,
          }}
        >
        QUOTA HEADERS,VENDOR ALLOCATIONS & CONTRACT DETAILS
          
          
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              fontSize: "12px",
              fontWeight: 600,
              color: "#374151",
              minWidth: "40px",
            }}
          >
            <span style={{ fontSize: "20px", fontWeight: 700, color: "#111827" }}>
              {selectedCount}
            </span>
            <span>selected</span>
          </div>

          <ButtonComponent
            label="Duplicate Selected"
            bgColor="#fff"
            color="#111827"
            border="1px solid #d1d5db"
            disabled={selectedCount === 0 || saveLoading || isCalculating}
            onClick={duplicateSelected}
          />
          <ButtonComponent
            label={isCalculating ? "Calculating..." : "Calculate"}
            bgColor="#111827"
            color="#fff"
            border="1px solid #d1d5db"
            disabled={!hasPendingCalculation || saveLoading || isCalculating || isAnyCellEditing}
            onClick={calculateUpdatedRows}
          />
          <ButtonComponent
            label="Save"
            bgColor="#111827"
            color="#fff"
            border="1px solid #d1d5db"
            disabled={!hasUpdatedRows || saveLoading || isCalculating || isAnyCellEditing}
            onClick={saveUpdatedRows}
          />
          <ButtonComponent
            label="Submit Selected"
            bgColor="#111827"
            color="#fff"
            disabled={selectedCount === 0 || saveLoading || isCalculating || isAnyCellEditing || hasUpdatedRows}
          //   maxWidth="140px"
            onClick={submitSelected}
          />
          <ButtonComponent
            label="Audit Log"
            bgColor="#fff"
            color="#111827"
            border="1px solid #d1d5db"
            maxWidth="110px"
            onClick={auditLog}
          />
        </div>
      </div>

      {/* ================= MASTER GRID ================= */}
      <div
        className="ag-theme-quartz"
        style={{
          width: "100%",
          borderRadius: "0 0 10px 10px",
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        <DataTable
          height={masterGridHeight}
          rows={filteredRowData}
          columns={columnDefs}
          paginationProp={true}
          paginationPageSize={masterPaginationPageSize}
          paginationPageSizeSelector={[10, 20, 30, 40]}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          onSelectionChanged={onSelectionChanged}
          onGridReady={(params) => setGridApi(params.api)}
          onCellValueChanged={onMasterCellValueChanged}
          onCellEditingStarted={handleCellEditingStarted}
          onCellEditingStopped={handleCellEditingStopped}
          getRowId={(params) => String(params.data.id)}
          defaultColDef={{
            sortable: false,
            filter: "agMultiColumnFilter",
            resizable: true,
          }}
          gridOptions={{
            masterDetail: true,
            detailCellRenderer: DetailCellRenderer,
            detailRowAutoHeight: true,
            headerHeight: 60,
            rowHeight: 94,
            animateRows: true,
            enableBrowserTooltips: true,
            tooltipShowDelay: 0,
            suppressCellFocus: true,
            suppressMovableColumns: true,
          }}
        />
      </div>

      <HistoryLog
        open={isHistoryLogOpen}
        onClose={closeHistoryLog}
        rows={historyLogData}
        loading={historyLoading}
      />
    </div>
  );
}